import type { BaremetalInstance } from '../@types/instance';
import type { BaremetalImage } from '../@types/launch';

export function priceForPeriod(
    b: Pick<BaremetalInstance, 'payment_period' | 'price_weekly' | 'price_monthly' | 'price_yearly'>
): number {
    if (b.payment_period === 'weekly') return b.price_weekly;
    if (b.payment_period === 'yearly') return b.price_yearly;
    return b.price_monthly;
}

export interface ResolvedBaremetalImage {
    os: string;
    version: string;
    defaultUsername: string;
}

/** Maps an instance's image slugs to display labels; unknown slugs fall back to the raw slug and an empty username. */
export function resolveImageLabel(
    images: BaremetalImage | undefined,
    slug: string,
    versionSlug: string
): ResolvedBaremetalImage {
    const image = images?.images?.find((i) => i.slug === slug);
    const version = image?.versions?.find((v) => v.slug === versionSlug);
    return {
        os: image?.label || slug,
        version: version?.label || versionSlug,
        defaultUsername: version?.defaultUsername ?? '',
    };
}

/** Display name for an image, e.g. "Ubuntu 24.04 LTS"; version labels that already start with the OS name ("Windows Server 2022") are used alone. */
export function imageOsLabel({ os, version }: ResolvedBaremetalImage): string {
    if (version.toLowerCase().startsWith(os.toLowerCase())) return version || os;
    return `${os} ${version}`.trim();
}

/** Average minutes from deploy to ready (SSH/RDP answering) on SSD nodes, measured on DDC hardware 2026-10; includes the ~3-4 min reboot/POST. Keyed by image slug. */
const INSTALL_MINUTES: Record<string, number> = {
    debian: 8,
    ubuntu: 11,
    'rocky-linux': 11,
    'alma-linux': 11,
    'windows-server': 13,
};

/** Average install time for an image, or null when it has not been measured. */
export function installMinutes(imageSlug: string | undefined): number | null {
    return (imageSlug && INSTALL_MINUTES[imageSlug]) || null;
}

export function shortCpuLabel(cpu: string): string {
    return cpu.replace(/^Intel\(R\) Xeon\(R\) CPU\s*/i, '');
}

export interface ParsedCpu {
    model: string;
    count: number;
    cores: number | null;
    clock: string | null;
}

/** Splits a package `cpu_label` like "Intel(R) Xeon(R) CPU E5-2680 v3 @ 2.50GHz ... x2 24 Core" into display parts; `coreLabel` wins over the core count in the label when set. */
export function parseCpuLabel(label: string, coreLabel?: string): ParsedCpu {
    const [head] = label.split(' @ ');
    const model =
        head
            .replace(/\(R\)|\(TM\)|™|®/gi, '')
            .replace(/\bCPU\b/i, '')
            .replace(/^\s*(Intel|AMD)\s+/i, '')
            .replace(/\s+/g, ' ')
            .trim() || label;
    const coresFrom = (s: string | undefined) => {
        const m = s?.match(/(\d+)\s*Core/i) ?? s?.match(/^\s*(\d+)\s*$/);
        return m ? Number(m[1]) : null;
    };
    return {
        model,
        count: Number(label.match(/\bx(\d+)\b/i)?.[1] ?? 1),
        cores: coresFrom(coreLabel) ?? coresFrom(label),
        clock: label.match(/@\s*([\d.]+\s*GHz)/i)?.[1].replace(/\s+/g, '') ?? null,
    };
}

export function memoryGb(label: string): number {
    return Number(label.match(/[\d.]+/)?.[0] ?? 0);
}

export type PackageSortKey = 'priceAsc' | 'priceDesc' | 'memoryDesc' | 'coresDesc';

type SortablePackage = Pick<
    BaremetalInstance,
    'price_weekly' | 'price_monthly' | 'price_yearly'
> & { cpu_label: string; core_label: string; memory_label: string };

export function sortPackages<T extends SortablePackage>(
    list: readonly T[],
    key: PackageSortKey,
    period: BaremetalInstance['payment_period']
): T[] {
    const price = (p: T) => priceForPeriod({ ...p, payment_period: period });
    const cores = (p: T) => parseCpuLabel(p.cpu_label, p.core_label).cores ?? 0;
    const compare: Record<PackageSortKey, (a: T, b: T) => number> = {
        priceAsc: (a, b) => price(a) - price(b),
        priceDesc: (a, b) => price(b) - price(a),
        memoryDesc: (a, b) =>
            memoryGb(b.memory_label) - memoryGb(a.memory_label) || price(a) - price(b),
        coresDesc: (a, b) => cores(b) - cores(a) || price(a) - price(b),
    };
    return [...list].sort(compare[key]);
}

/** Splits a spec label like "64 GB (DDR4)" into `{ value: '64 GB', detail: 'DDR4' }`; labels without a trailing "(…)" have an empty detail. */
export function splitSpecLabel(label: string): { value: string; detail: string } {
    const m = label.match(/^(.*?)\s*\(([^()]*)\)\s*$/);
    return m ? { value: m[1], detail: m[2] } : { value: label, detail: '' };
}
